import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  Button,
  InvitePending,
  InviteUsers,
  InviteUsersProps,
  LoaderXs,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import React, { useEffect, useState } from 'react';
import { API, endpoints } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { PLANRULES } from 'src/constants/plans';
import { useWorkspaceStore } from 'src/features/workspace/store/useWorkspaceStore';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getAllOrgMembers } from 'src/utils/getMemberDetails';
import useSWR from 'swr';

import { useWorkspaceAccess } from '../hooks/useWorkspaceAccess';
import { AccessDropdown, UsersResultsShare } from '../share/sub-components';

export interface Member {
  access: string;
  member: {
    memberId: string;
    firstname: string;
    lastname: string;
    email: string;
    userType: string;
    avatar: string;
  };
}
export const ManageMembers = ({
  admin,
  accessMenuItems,
  workspaceId,
  workspaceName,
  workspaceaccess,
  isPublic,
}: {
  guests?: Member[];

  admin?: boolean;
  isPublic?: boolean;
  workspaceaccess?: string;
  accessMenuItems: { title: string; value: string }[];
  workspaceName: string;
  workspaceId: string;
  handleToggleWorkspace: (value: boolean) => void;
}): JSX.Element => {
  const [isRemoveUserOpen, setIsRemoveUserOpen] = useState(false);
  const authData = useAuth((s) => s.userDetails);
  const plan = authData?.plan?.type || '';
  const [deleteMember, setDeleteMember] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const { data: guestList } = useSWR('/user/guests', API.get);

  const { workspace, actions, isLoading } = useWorkspaceStore();
  const [openSnackbar] = useSnackbar();

  const members = workspace?.members?.filter((obj) => obj.member.role !== 'guest') || [];
  const guests = workspace?.guests || [];
  const { isFullAccess } = useWorkspaceAccess();

  useEffect(() => {
    actions.fetchWorkspace(workspaceId);
  }, []);

  const handleRemoveUserClick = async (id: string, value: string) => {
    if (value === 'remove') {
      setIsRemoveUserOpen(true);
      setDeleteMember(id);
    } else {
      const response = await API.put('/workspace/member', {
        id: workspaceId,
        memberId: id,
        access: value,
      });
      if (response.data) {
        actions.fetchWorkspace(workspaceId);
        openSnackbar({
          text: 'Member access updated',
          snackType: 'standard',
          position: 'bottom-center',
        });
      }
    }
  };
  const handleRemoveUserModalClose = () => {
    setIsRemoveUserOpen(false);
    setDeleteMember('');
  };

  const handleInviteUsers: InviteUsersProps['onSubmit'] = async (data) => {
    setIsInviteOpen(false);
    const payload = {
      id: workspaceId,
      members: data.items.map((obj) => obj.memberId),
      emails: data.items.filter((obj) => obj.isNew).map((obj) => obj.email),
    };
    const response = API.post(endpoints.workspaces.addWorkspaceMember.url, payload);
    if ((await response).data.success) {
      Analytics.track('Member added to workspace', {
        'Workspace name': workspaceName,
        'No. of members': workspace?.members?.length,
        'Workspace id': workspaceId,
      });
      actions.fetchWorkspace(workspaceId);

      openSnackbar({
        text: 'Member added to workspace',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };
  const handleRemoveUser = async () => {
    const response = await API.delete(
      `/workspace/member?id=${workspaceId}&memberId=${deleteMember}`
    );

    if (response.data.success) {
      setIsRemoveUserOpen(false);

      actions.fetchWorkspace(workspaceId);
      openSnackbar({
        text: 'Member removed from workspace',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  return (
    <section>
      <section className="pl-16 pr-32">
        <h3 className="font-medium leading-32 text-xs mb-8 text-gray-600">Members</h3>

        <div className="flex items-center">
          <Tippy
            disabled={isFullAccess}
            className="w-auto h-auto bg-gray-999"
            content={'Only the organization admin or users with full access can add other members'}
            arrow={true}
          >
            <span className="inline-block">
              <Button
                disabled={!isFullAccess}
                onClick={() => isFullAccess && setIsInviteOpen(true)}
                status="primary"
              >
                {isPublic ? 'Add guest' : 'Add member'}
              </Button>
            </span>
          </Tippy>
        </div>
      </section>
      {isLoading ? (
        <LoaderXs className="mx-auto text-xss mt-110" />
      ) : (
        <section className="mt-10 pl-16 pr-32 pb-32">
          {members.length >= 0 && (
            <h3 className="font-regular leading-32 text-xs text-gray-600">
              Members in {workspaceName} ({members.length + 1})
            </h3>
          )}

          {members.map(({ access, member, role }) => (
            <div key={member.memberId} className="flex justify-between items-center">
              <UsersResultsShare
                className="members"
                email={member.email}
                username={member.firstname + ' ' + member.lastname}
                id={member.memberId}
                avatar={member.avatar}
                role={role}
              />

              <AccessDropdown
                onClickMenuItem={(values) => handleRemoveUserClick(member.memberId, values)}
                menuItems={accessMenuItems}
                isAdmin={role === 'admin'}
                accessLevel={access}
                isReadOnly={!isFullAccess || isPublic}
              />
            </div>
          ))}

          {workspace.owner && (
            <div className="flex justify-between items-center">
              <UsersResultsShare
                className="members"
                email={workspace?.owner?.email}
                username={workspace?.owner?.firstname + ' ' + workspace?.owner?.lastname}
                id={workspace?.owner?.memberId}
                avatar={workspace?.owner?.avatar}
              />

              <p className="text-sm text-gray-600">Workspace owner</p>
            </div>
          )}

          {!!guests.length && (
            <>
              <h3 className="font-regular leading-32 text-xs text-gray-600 mt-10">
                Guests ({guests.length})
              </h3>
              {/* <div className="flex justify-between pb-5 border-b-2 border-gray-100"> */}
              {/* <div className="text-sm font-regular">User</div> */}
              {/* </div> */}
              <div className="max-h-275 overflow-y-auto no-scrollbar">
                {guests.map(({ member, access }) => (
                  <div key={member?.memberId} className="flex justify-between items-center">
                    <UsersResultsShare
                      className="members"
                      avatar={member?.avatar}
                      email={member?.email}
                      username={member?.firstname}
                      id={member?.memberId}
                    />
                    <AccessDropdown
                      onClickMenuItem={(values) => handleRemoveUserClick(member.memberId, values)}
                      menuItems={accessMenuItems}
                      isAdmin={member.role === 'admin'}
                      accessLevel={access}
                      isReadOnly={!(workspaceaccess === 'full' || admin)}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
          <div>
            {workspace?.invites?.length > 0 && (
              <div className="-mt-10">
                {workspace.invites?.map(({ email, access }) => (
                  <InvitePending email={email} role={access} key={email} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/** Remove User Modal  */}
      <Modal isOpen={isRemoveUserOpen} onClose={handleRemoveUserModalClose}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '31.2rem',
            borderRadius: '0.8rem',
            margin: 'auto',
            backgroundColor: 'white',
            padding: '24px',
          }}
        >
          <ModalCloseButton className="text-xs text-gray-500 mb-24" />
          <ModalBody>
            <div>
              <p className="font-medium text-gray text-lg mb-16">Remove User</p>
              <p className="text-sm mb-16 text-gray-600">
                Are you sure?&nbsp;
                <span className="text-primary-500">
                  {
                    workspace?.members?.filter(({ member }) => member.memberId === deleteMember)[0]
                      ?.member.firstname
                  }
                </span>
                &nbsp;will no longer have editor access in the
                <span className="text-primary-500"> {workspaceName} </span> team
              </p>
              <div className="flex justify-center">
                <Button
                  status="secondary"
                  size="lg"
                  className="mr-12"
                  onClick={handleRemoveUserModalClose}
                >
                  Cancel
                </Button>
                <Button status="primary" size="lg" onClick={handleRemoveUser}>
                  Remove
                </Button>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
      {/** Invite User Modal  */}
      <Modal isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)}>
        <ModalOverlay />
        <ModalContent
          maxWidth="50rem"
          borderRadius="0.8rem"
          className="bg-white static rounded-lg"
          margin="auto"
        >
          <ModalBody className="flex" p="0">
            <div className="w-full">
              <InviteUsers
                members={
                  !isPublic
                    ? getAllOrgMembers()
                        ?.filter((obj) => obj.memberId !== authData?.memberInformation?.memberId)
                        .map((obj) => ({
                          firstname: obj.firstname,
                          email: obj.email,
                          type: obj.role,
                          avatar: obj.avatar,
                          memberId: obj.memberId,
                        }))
                    : guestList?.data?.data
                        ?.filter(
                          (obj) => obj.guest.memberId !== authData?.memberInformation?.memberId
                        )
                        .map((obj) => ({
                          firstname: obj.guest.firstname,
                          email: obj.guest.email,
                          type: obj.guest.role,
                          avatar: obj.guest.avatar,
                          memberId: obj.guest.memberId,
                        })) || []
                }
                accessLevels={[
                  { label: 'Admin', value: 'admin' },
                  { label: 'Member', value: 'member' },
                ]}
                hasAccessLevels={false}
                userInviteLimit={PLANRULES.guests[plan]}
                guestCount={guestList?.data?.data?.length}
                onSubmit={(values) => handleInviteUsers(values)}
                isFreeTrial={!!authData?.plan?.trial}
              />
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </section>
  );
};
